import React, { useEffect, useMemo } from "react";
import { Navigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

const createLoginSchema = (t) =>
  z.object({
    userId: z
      .string()
      .min(1, t("auth.userIdRequired"))
      .min(3, t("auth.userIdMin"))
      .max(20, t("auth.userIdMax"))
      .regex(/^[a-zA-Z0-9_]+$/, t("auth.userIdPattern")),
    password: z
      .string()
      .min(1, t("auth.passwordRequired"))
      .min(4, t("auth.passwordMin"))
      .max(50, t("auth.passwordMax")),
    rememberUserId: z.boolean().optional(),
  });

export default function LoginPage() {
  const { login, loading, user } = useAuth();
  const { setLanguage, t } = useI18n();
  const loginSchema = useMemo(() => createLoginSchema(t), [t]);
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: { userId: "", password: "", rememberUserId: false },
  });

  useEffect(() => {
    setLanguage("en");
  }, [setLanguage]);

  useEffect(() => {
    const savedUserId = localStorage.getItem("rememberedUserId");
    const isRemembered = localStorage.getItem("rememberUserId") === "true";
    if (savedUserId && isRemembered) {
      setValue("userId", savedUserId);
      setValue("rememberUserId", true);
    }
  }, [setValue]);

  if (user) return <Navigate to="/main" replace />;

  const onSubmit = async (data) => {
    clearErrors();
    if (data.rememberUserId) {
      localStorage.setItem("rememberedUserId", data.userId);
      localStorage.setItem("rememberUserId", "true");
    } else {
      localStorage.removeItem("rememberedUserId");
      localStorage.setItem("rememberUserId", "false");
    }

    try {
      const result = await login(data.userId, data.password);
      if (!result.success) {
        setError("root.serverError", { type: "manual", message: result.error });
      }
    } catch {
      setError("root.serverError", {
        type: "manual",
        message: t("auth.loginFailed"),
      });
    }
  };

  return (
    <div className="flex h-screen overflow-y-auto">
      <div className="relative hidden lg:flex lg:w-1/2">
        <div
          className="w-full bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url('/images/login_sub.png')" }}
        >
          <div className="absolute inset-0 bg-black/20" />
          <div className="absolute bottom-8 left-8 text-white">
            <h1 className="mb-4 text-4xl font-bold">EDI Portal</h1>
            <p className="whitespace-pre-line text-lg opacity-90">
              {t("auth.intro")}
            </p>
          </div>
        </div>
      </div>

      <div className="flex w-full items-center justify-center bg-white px-6 lg:w-1/2">
        <div className="w-full max-w-md">
          <Card className="border border-orange-100 bg-white/90 shadow-2xl backdrop-blur-md">
            <CardHeader className="pb-6 text-center">
              <CardTitle className="mb-2 text-3xl font-bold text-orange-900">
                {t("auth.login")}
              </CardTitle>
              <CardDescription className="text-orange-700">
                {t("auth.loginDescription")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="userId" className="font-medium text-orange-800">
                    {t("auth.userId")}
                  </Label>
                  <Input
                    id="userId"
                    type="text"
                    {...register("userId")}
                    autoComplete="username"
                    disabled={loading}
                    className="h-12 border-orange-200 bg-orange-50/50 text-orange-900"
                  />
                  {errors.userId && (
                    <p className="text-sm text-red-600">{errors.userId.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="font-medium text-orange-800">
                    {t("auth.password")}
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    {...register("password")}
                    placeholder={t("auth.passwordPlaceholder")}
                    autoComplete="current-password"
                    disabled={loading}
                    className="h-12 border-orange-200 bg-orange-50/50 text-orange-900"
                  />
                  {errors.password && (
                    <p className="text-sm text-red-600">{errors.password.message}</p>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    id="rememberUserId"
                    type="checkbox"
                    {...register("rememberUserId")}
                    disabled={loading}
                    className="h-4 w-4 rounded border-orange-300 text-orange-600"
                  />
                  <Label htmlFor="rememberUserId" className="cursor-pointer text-sm font-normal text-orange-700">
                    {t("auth.rememberUserId")}
                  </Label>
                </div>
                {errors.root?.serverError && (
                  <div className="rounded-lg border border-red-300 bg-red-100 p-3 text-sm text-red-700">
                    {errors.root.serverError.message}
                  </div>
                )}
                <Button
                  type="submit"
                  className="h-12 w-full bg-gradient-to-r from-orange-600 to-amber-600 font-semibold text-white"
                  disabled={loading}
                >
                  {loading ? t("auth.loggingIn") : t("auth.login")}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
